import { throwIfTilesAborted } from '@/plugins/tiles/TilesError'

export interface TileImageSize {
  readonly width: number
  readonly height: number
}

export function getDecodedImageSize(image: TexImageSource): TileImageSize {
  if ('naturalWidth' in image) return { width: image.naturalWidth, height: image.naturalHeight }
  if ('videoWidth' in image) return { width: image.videoWidth, height: image.videoHeight }
  if ('displayWidth' in image) return { width: image.displayWidth, height: image.displayHeight }
  return { width: image.width, height: image.height }
}

function closeImage(image: TexImageSource): void {
  if ('close' in image) image.close()
  else if ('getContext' in image) image.width = image.height = 0
}

/** 接管输入所有权，缩放、取消或失败时回收旧图片；返回结果交给内容包。 */
export async function resizeTileImage(
  image: TexImageSource,
  target: TileImageSize,
  signal: AbortSignal
): Promise<TexImageSource> {
  let transferred = false
  try {
    throwIfTilesAborted(signal)
    const actual = getDecodedImageSize(image)
    if (actual.width <= target.width && actual.height <= target.height) {
      transferred = true
      return image
    }
    const scale = Math.min(1, target.width / actual.width, target.height / actual.height)
    // 兼容不支持/忽略 bitmap resize 的环境；不允许默默上传原始大图。
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.floor(actual.width * scale))
    canvas.height = Math.max(1, Math.floor(actual.height * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('纹理缩放需要可用的2D画布')
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    try {
      if ('data' in image) {
        // TexImageSource允许自定义解码器返回ImageData，drawImage不能直接接收它。
        const staging = document.createElement('canvas')
        staging.width = actual.width
        staging.height = actual.height
        try {
          const stagingContext = staging.getContext('2d')
          if (!stagingContext) throw new Error('纹理像素缩放需要可用的2D画布')
          stagingContext.putImageData(image, 0, 0)
          context.drawImage(staging, 0, 0, canvas.width, canvas.height)
        } finally {
          staging.width = staging.height = 0
        }
      } else context.drawImage(image as CanvasImageSource, 0, 0, canvas.width, canvas.height)
      throwIfTilesAborted(signal)
    } catch (error) {
      canvas.width = canvas.height = 0
      throw error
    }
    return canvas
  } finally {
    if (!transferred) closeImage(image)
  }
}

/** 优先在浏览器图片解码时直接缩小，避免把完整大位图交给Three。 */
export async function decodeTileImage(
  data: ArrayBuffer,
  mimeType: string,
  signal: AbortSignal,
  target: TileImageSize
): Promise<TexImageSource> {
  throwIfTilesAborted(signal)
  const blob = new Blob([data], { type: mimeType })
  if (typeof createImageBitmap === 'function') {
    // glTF左上角原点，不翻转、不预乘alpha、不应用EXIF或色彩转换。
    const options: ImageBitmapOptions = {
      imageOrientation: 'none',
      premultiplyAlpha: 'none',
      colorSpaceConversion: 'none',
    }
    let bitmap: ImageBitmap
    try {
      bitmap = await createImageBitmap(blob, {
        ...options,
        resizeWidth: target.width,
        resizeHeight: target.height,
        resizeQuality: 'high',
      })
    } catch {
      throwIfTilesAborted(signal)
      // 老浏览器可能拒绝resize选项；完整解码后通过2D画布执行同样上限。
      bitmap = await createImageBitmap(blob, options)
    }
    return resizeTileImage(bitmap, target, signal)
  }
  const url = URL.createObjectURL(blob)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const value = new Image()
      const cleanup = () => {
        signal.removeEventListener('abort', abort)
        value.onload = null
        value.onerror = null
      }
      const abort = () => {
        cleanup()
        value.src = ''
        reject(new DOMException('图片解码已取消', 'AbortError'))
      }
      value.onload = () => {
        cleanup()
        resolve(value)
      }
      value.onerror = () => {
        cleanup()
        reject(new Error('PNG/JPEG/WebP 图片解码失败'))
      }
      signal.addEventListener('abort', abort, { once: true })
      if (signal.aborted) abort()
      else value.src = url
    })
    return await resizeTileImage(image, target, signal)
  } finally {
    URL.revokeObjectURL(url)
  }
}
